<script setup>
import { defineVaDataTableColumns, VaBadge, VaDataTable, VaPagination } from 'vuestic-ui';
import VaChart from '../va-charts/VaChart.vue';
import { computed, ref } from 'vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  companies: {
    type: Array,
    default: () => [],
  },
  startDate: {
    type: String,
    default: null,
  },
  endDate: {
    type: String,
    default: null,
  },
})

const reloadChart = ref(false);
const currentPage = ref(1);
const perPage = ref(20);

const pages = computed(() => {
  return Math.ceil(props.data.length / perPage.value);
});

const columns = defineVaDataTableColumns([
  { label: 'Ação', key: 'action' },
  // { label: 'Empresa', key: 'company' },
  { label: 'Nome', key: 'name' },
  { label: 'E-mail', key: 'email' },
  { label: 'Telefone', key: 'phone' },
])

function getCompanyName(tenant_id) {
  const company = props.companies.find(c => c.id === tenant_id);
  return company ? company.name : 'N/A';
}

const monthDays = computed(() => {
  const daysInMonth = [];
  const start = props.startDate ? new Date(props.startDate + 'T00:00:00') : null;
  const end = props.endDate ? new Date(props.endDate + 'T00:00:00') : null;

  if (start && end) {
    if (start.getMonth() !== end.getMonth() || start.getFullYear() !== end.getFullYear()) {
      return daysInMonth;
    }

    const current = new Date(start);
    while (current <= end) {
      daysInMonth.push(new Date(current).getDate().toString().padStart(2, '0'));
      current.setDate(current.getDate() + 1);
    }
  }

  return daysInMonth;
});

const yearMonths = computed(() => {
  const monthsInYear = [];
  const start = props.startDate ? new Date(props.startDate + 'T00:00:00') : null;
  const end = props.endDate ? new Date(props.endDate + 'T00:00:00') : null;

  if (start && end) {
    if (start.getMonth() === end.getMonth()) {
      return monthsInYear;
    }

    for (let month = start.getMonth(); month <= end.getMonth(); month++) {
      monthsInYear.push((month + 1).toString().padStart(2, '0'));
    }
  }

  return monthsInYear;
});

const labels = computed(() => {
  return monthDays.value.length ? monthDays.value : yearMonths.value;
});

const createdDataValues = computed(() => {
  const data = [];
  labels.value.forEach(label => {
    const count = props.data.filter(log => {
      if (log.action !== 'created') return false;

      const logDate = new Date(log.created_at);
      const logDay = logDate.getDate().toString().padStart(2, '0');
      const logMonth = (logDate.getMonth() + 1).toString().padStart(2, '0');

      if (monthDays.value.length) {
        return logDay === label;
      } else if (yearMonths.value.length) {
        return logMonth === label;
      }
      return false;
    }).length;
    data.push(count);
  });
  return data;
});

const deletedDataValues = computed(() => {
  const data = [];
  labels.value.forEach(label => {
    const count = props.data.filter(log => {
      if (log.action !== 'deleted') return false;

      const logDate = new Date(log.created_at);
      const logDay = logDate.getDate().toString().padStart(2, '0');
      const logMonth = (logDate.getMonth() + 1).toString().padStart(2, '0');

      if (monthDays.value.length) {
        return logDay === label;
      } else if (yearMonths.value.length) {
        return logMonth === label;
      }
      return false;
    }).length;
    data.push(count);
  });
  return data;
});

const createdDataset = computed(() => [
  {
    label: 'Usuários Criados',
    backgroundColor: 'rgba(54, 162, 235, 0.6)',
    data: createdDataValues.value,
  },
]);

const totalCreatedUsers = computed(() => {
  return createdDataValues.value.reduce((a, b) => a + b, 0);
});

const deletedDataset = computed(() => [
  {
    label: 'Usuários Removidos',
    backgroundColor: 'rgba(255, 99, 132, 0.6)',
    data: deletedDataValues.value,
  },
]);

const totalDeletedUsers = computed(() => {
  return deletedDataValues.value.reduce((a, b) => a + b, 0);
});

const options = {
  scales: {
    x: {
      display: false,
      grid: {
        display: false,
      },
    },
    y: {
      display: false,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
    },
  },
}
</script>

<template>
  <div class="space-y-8">
    <div>
      <div class="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h3 class="text-lg font-bold">Usuários criados ({{ totalCreatedUsers }})</h3>
          <div>
            <VaChart v-if="!reloadChart" :data="{
              labels: labels,
              datasets: createdDataset
            }" class="h-24" type="line" :options="options" />
          </div>
        </div>

        <div>
          <h3 class="text-lg font-bold">Usuários removidos ({{ totalDeletedUsers }})</h3>

          <div>
            <VaChart v-if="!reloadChart" :data="{
              labels: labels,
              datasets: deletedDataset
            }" class="h-24" type="line" :options="options" />
          </div>
        </div>
      </div>
    </div>

    <VaDataTable :wrapper-size="400" :item-size="40" virtual-scroller sticky-header :per-page="perPage"
      :current-page="currentPage" hoverable :items="data" :columns="columns">
      <template #cell(action)="{ rowData }">
        <div class="flex items-center gap-2">
          <VaBadge v-if="rowData.action == 'created'" text="Criado" color="success" />
          <VaBadge v-if="rowData.action == 'deleted'" text="Removido" color="danger" />
        </div>
      </template>

      <template #cell(name)="{ rowData }">
        <div class="flex items-center gap-2">
          <span v-if="rowData.action == 'created'">{{ rowData.new_values.name }}</span>
          <span v-else-if="rowData.action == 'deleted'">{{ rowData.old_values.name }}</span>
        </div>
      </template>

      <template #cell(email)="{ rowData }">
        <div class="flex items-center gap-2">
          <span v-if="rowData.action == 'created'">{{ rowData.new_values.email }}</span>
          <span v-else-if="rowData.action == 'deleted'">{{ rowData.old_values.email }}</span>
        </div>
      </template>

      <template #cell(phone)="{ rowData }">
        <div class="flex items-center gap-2">
          <span v-if="rowData.action == 'created'">{{ rowData.new_values.phone }}</span>
          <span v-else-if="rowData.action == 'deleted'">{{ rowData.old_values.phone }}</span>
        </div>
      </template>

      <template #bodyAppend>
        <tr>
          <td colspan="6">
            <div class="flex justify-center mt-4">
              <VaPagination v-model="currentPage" :pages="pages" />
            </div>
          </td>
        </tr>
      </template>
    </VaDataTable>
  </div>
</template>

<style scoped></style>
